// @ts-strict-ignore
import React, { useCallback, useEffect } from 'react';
import {
  Box, Button, Text, useSnackBar,
} from '@usealloy/component-library';
import type { SubmitHandler } from 'react-hook-form';
import { useForm, Controller } from 'react-hook-form';
import OtpInput from 'react-otp-input';
import { routes } from '~/helpers/routes';
import { GRAY, RED } from '~/constants';
import {
  cancelExistingVerification,
  getTimezoneAuthData,
  VERIFICATION_ID_KEY,
} from '../../helpers';
import { ajaxFetch, buildURLParamsString } from '~/helpers/api';
import log from '~/helpers/logging';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

interface IFormInput {
  oneTimeTotp: string;
}

const TotpVerifyView = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { tokenUid, token } = useParams();
  const addSnackBar = useSnackBar();

  const isTotpFlow = location?.pathname?.includes(
    `/mfa/totp/${tokenUid}/${token}`,
  );

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    setError,
    formState: { errors },
  } = useForm<IFormInput>({
    defaultValues: {
      oneTimeTotp: '',
    },
  });

  const watchOneTimeTotp = watch('oneTimeTotp');

  const onSubmit: SubmitHandler<IFormInput> = async formData => {
    const nextUrl = location?.state?.nextUrl || '/';

    const sid = localStorage.getItem('totpSid');

    const genericErrorMessage = 'Something went wrong while verifying the code';

    let endpointUrl;
    let requestPayload;

    if (location?.state?.from === 'mfa-setup-totp') {
      endpointUrl = routes.authentication.mfa.verifyCodeTotp(tokenUid, token);
      requestPayload = {
        mfa_code: formData.oneTimeTotp,
        sid,
        next_url: nextUrl,
        ...getTimezoneAuthData(),
      };
    } else if (location?.state?.from === 'mfa-setup-verify-phonenumber') {
      endpointUrl = routes.authentication.mfa.verifyNumber(tokenUid, token);
      requestPayload = {
        mfa_code: formData.oneTimeTotp, // this is the name of the field but in this case it comes from the phone
        phone: location.state.phoneNumber,
        next_url: nextUrl,
        ...getTimezoneAuthData(),
      };
    } else {
      endpointUrl = routes.authentication.mfa.verifyCode(tokenUid, token);
      requestPayload = {
        mfa_code: formData.oneTimeTotp,
        sid,
        next_url: nextUrl,
        ...getTimezoneAuthData(),
      };
    }

    try {
      const response = await ajaxFetch(endpointUrl, {
        method: 'POST',
        body: JSON.stringify(requestPayload),
        camelCase: true,
        enhancedErrorResponse: true,
      });

      const redirectDestination = response.redirectUrl || '/evaluations';

      // NOTE: if this redirect changes on the backend, the snackbar won't confirmation when we return to sign-in
      if (
        (redirectDestination === '/'
          || redirectDestination === '/v3/dashboard/home/')
        && (location?.state?.from === 'mfa-setup-totp'
          || location?.state?.from === 'mfa-setup-verify-phonenumber')
      ) {
        sessionStorage.setItem('oneTimeTotpSuccess', 'true');
      }

      window.location.replace(redirectDestination);
    } catch (err) {
      switch (err?.result) {
        case 'error_account_inactive':
          navigate(routes.v3.dashboard.authentication.login, {
            replace: true,
            state: {
              message: {
                text: err.message,
                type: 'danger',
              },
            },
          });
          break;
        case 'error_password_expired': {
          const tokenString = err.redirectUrl.match(
            /([0-9A-Za-z_-]+\/[0-9A-Za-z]{1,13}-[0-9A-Za-z]{1,40})/g,
          )[0];
          navigate(routes.v3.dashboard.authentication['reset-password'](tokenString), {
            replace: true,
            state: {
              nextUrl,
              message: {
                text: err.message,
                type: 'danger',
              },
            },
          });
          break;
        }
        default:
          addSnackBar({
            title: err.message || genericErrorMessage,
            type: 'danger',
          });
          setError('oneTimeTotp', {
            type: 'manual',
            message: err?.message || genericErrorMessage,
          });
          log('error', `GET ${endpointUrl} exception`, {}, err);
      }
    }
  };

  const requestMFACode = useCallback(
    async (sms = false) => {
      try {
        await cancelExistingVerification();
        const endpoint = routes.authentication.mfa.verifyCode(tokenUid, token);
        const param = buildURLParamsString({ sms });
        const response = await ajaxFetch(`${endpoint}${param}`);
        if (response) {
          if (sms) {
            addSnackBar({
              title:
                'MFA code is requested and being sent to the registered phone number.',
              type: 'success',
            });
          }

          if (response.verification) {
            // Record the SID of the verification in localStorage if we need to cancel and create
            // a new one
            window.localStorage.setItem(
              VERIFICATION_ID_KEY,
              response.verification.sid,
            );
          }
        }
      } catch (error) {
        addSnackBar({
          title:
            'Something went wrong while requesting MFA code. Please try again or contact support.',
          type: 'danger',
        });
        log(
          'error',
          `GET ${routes.authentication.mfa.verifyCode(
            tokenUid,
            token,
          )} exception`,
          {},
          error,
        );
      }
    },
    [addSnackBar, token, tokenUid],
  );

  useEffect(() => {
    // If user has entered 6 digits in TOTP, submit form
    if (watchOneTimeTotp.length === 6) {
      handleSubmit(onSubmit)();
    }
  }, [watchOneTimeTotp]);

  useEffect(() => {
    if (
      location?.state?.from !== 'mfa-setup-verify-phonenumber'
      && location?.pathname?.includes(`/mfa/${tokenUid}/${token}`)
    ) {
      requestMFACode(true);
    }
  }, [location, requestMFACode]);

  return (
    <Box direction="column" gap={10} style={{ width: '440px' }}>
      <Text as="h1" size={6} style={{ textAlign: 'center' }}>
        Enter your one-time code
      </Text>
      <Text size={4} style={{ textAlign: 'center' }}>
        {isTotpFlow
          ? 'Use the verification code generated by your authenticator application.'
          : 'Use the verification code sent to your phone number.'}
      </Text>
      <form style={{ width: '100%' }}>
        <Controller
          control={control}
          render={({ field }) => (
            // OTP FORM
            <OtpInput
              containerStyle={{
                width: '100%',
                justifyContent: 'space-between',
              }}
              inputStyle={{
                height: '80px',
                width: '64px',
                gap: '10px',
                padding: '10px 8px',
                borderRadius: '8px',
                fontSize: '36px',
                border: `1px solid ${
                  errors.oneTimeTotp ? RED.red500 : GRAY.gray500
                }`,
              }}
              value={field.value}
              onChange={e => setValue(field.name, e)}
              numInputs={6}
              inputType="tel"
              renderInput={props => <input {...props} />}
              shouldAutoFocus
            />
          )}
          name="oneTimeTotp"
        />
        {errors.oneTimeTotp && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: '8px',
            }}
          >
            <Text style={{ color: RED.red500 }}>Try again</Text>
          </div>
        )}
        <Box style={{ marginTop: '40px' }}>
          {!isTotpFlow && (
            <Button type="button" onClick={() => requestMFACode(true)}>
              Resend Code
            </Button>
          )}
        </Box>
      </form>
    </Box>
  );
};

export default TotpVerifyView;
